import cn from 'classnames';
import React, { FC, ReactElement, SyntheticEvent, memo } from 'react';

import { Tabs as MuiTabs, Tab, TabsProps, tabsClasses } from '@mui/material';

import { Size, TabType } from '@constants/common/ui.constants';

import useStyles from './styles.module';

interface Props extends TabsProps {
  options: {
    label: string;
    value: string | number;
    icon?: string | ReactElement;
    disabled?: boolean;
  }[];
  onChange: (_: SyntheticEvent, newValue: number | string) => void;
  activeTab: number | string;
  isScrollable?: boolean;
  fullWidth?: boolean;
  size?: Size;
  type?: TabType;
  className?: string;
  isDark?: boolean;
  isDisabled?: boolean;
}

const Tabs: FC<Props> = ({
  options,
  activeTab,
  isScrollable = false,
  fullWidth = false,
  onChange,
  size = Size.Small,
  type = TabType.Internal,
  className,
  variant,
  isDark = false,
  isDisabled = false,
}) => {
  const { classes } = useStyles({
    type,
    size,
    isDark,
    fullWidth,
  });

  return (
    <MuiTabs
      value={activeTab}
      onChange={onChange}
      className={cn(
        classes.tabs,
        {
          [classes.internal]: type === TabType.Internal,
          [classes.external]: type === TabType.External,
          [classes.embedded]: type === TabType.Embedded,
          [classes.text]: type === TabType.Text,
        },
        className,
      )}
      scrollButtons={isScrollable}
      aria-label="visible arrows tabs"
      variant={isScrollable ? 'scrollable' : (variant ?? 'standard')}
      sx={{
        [`& .${tabsClasses.scrollButtons}`]: {
          '&.Mui-disabled': { opacity: 0.3 },
        },
      }}
    >
      {options.map(({ label, value, icon, disabled }) => (
        <Tab
          icon={icon}
          key={label}
          label={label}
          value={value}
          iconPosition="end"
          disableFocusRipple
          disableTouchRipple
          disabled={disabled || isDisabled}
        />
      ))}
    </MuiTabs>
  );
};

export default memo(Tabs);
